/**
 * FiscalPrintButton — wydruk paragonu fiskalnego z dokumentu sprzedaży.
 *
 * To NIE jest to samo, co sąsiedni „Fiskalizuj": tamten tylko ODNOTOWUJE numer paragonu
 * wydrukowanego wcześniej na stanowisku Symfonii, ten faktycznie DRUKUJE — przez naszą usługę
 * stojącą przy drukarce u klienta.
 *
 * Trzy rzeczy, które ten ekran musi robić dobrze, bo paragonu nie da się cofnąć:
 *
 *  1. **Pyta o stanowisko przed drukiem** i pokazuje, czy usługa przy tej drukarce żyje.
 *     Wysłanie wydruku na martwe stanowisko to zadanie wiszące w kolejce i klient bez paragonu.
 *  2. **Pyta o NIP nabywcy PRZED, nie po.** Po wydruku NIP-u nie da się dopisać, a bez niego
 *     nie powstanie faktura do tego paragonu — mówimy to wprost, zamiast chować w podpowiedzi.
 *  3. **Stan „nie wiadomo" pokazuje inaczej niż błąd.** „Nie udało się" i „nie wiemy, czy
 *     paragon wyszedł" to dwie różne sytuacje; pomylenie ich kończy się drugim wydrukiem.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Printer } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import {
  type FiscalMappingErrorDto,
  type FiscalPrintJobState,
  getMyStation,
  getPrintJob,
  isPrintJobInProgress,
  listStations,
  queueReceiptPrint,
  setMyStation,
} from '@/lib/fiscal/fiscal-api';

interface Props {
  documentId: number | string;
  companyId: string | null;
  /** Dokument w buforze nie nadaje się na paragon — jego treść może się jeszcze zmienić. */
  inBuffer: boolean;
  fiscalized: boolean;
  buyerTaxId: string | null;
  onDone: () => void;
}

export function FiscalPrintButton({
  documentId,
  companyId,
  inBuffer,
  fiscalized,
  buyerTaxId,
  onDone,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [stationId, setStationId] = useState<string>('');
  /**
   * NIP nabywcy domyślnie WŁĄCZONY, gdy dokument go niesie (decyzja właściciela 2026-09-15).
   *
   * Bez NIP-u na paragonie nie powstanie do niego faktura, a tego nie da się cofnąć po wydruku.
   * Domyślne „nie" znaczyłoby, że przy każdym dokumencie dla firmy ktoś musi PAMIĘTAĆ
   * o zaznaczeniu — a zapomnienie wychodzi dopiero wtedy, gdy klient poprosi o fakturę.
   */
  const [withTaxId, setWithTaxId] = useState(!!buyerTaxId);
  /** Potwierdzenie świadomej rezygnacji z NIP-u przy dokumencie, który go ma. */
  const [confirmNoTaxId, setConfirmNoTaxId] = useState(false);
  /**
   * Dokument ma już swój paragon. Trzymamy to osobno od postępu wydruku, bo to NIE jest
   * wynik tego kliknięcia — tylko informacja o czymś, co zdarzyło się wcześniej.
   */
  const [alreadyPrinted, setAlreadyPrinted] = useState<{
    receiptNumber: string | null;
    printedAt: string | null;
  } | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [mappingErrors, setMappingErrors] = useState<FiscalMappingErrorDto[]>([]);

  const stationsQuery = useQuery({
    queryKey: ['fiscal', 'stations', companyId],
    queryFn: () => listStations(companyId!),
    enabled: open && !!companyId,
  });

  // Drukarka przypisana do TEGO użytkownika. Kasjer nie powinien wybierać jej przy każdym
  // paragonie — ale musi móc wybrać inną, bo kasy się psują i obsada się przesiada.
  const myStationQuery = useQuery({
    queryKey: ['fiscal', 'my-station', companyId],
    queryFn: () => getMyStation(companyId!),
    enabled: open && !!companyId,
  });

  useEffect(() => {
    const assigned = myStationQuery.data;
    if (assigned && !stationId) setStationId(assigned.stationId);
  }, [myStationQuery.data, stationId]);

  const rememberMutation = useMutation({
    mutationFn: () => setMyStation(companyId!, stationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fiscal', 'my-station'] });
      toast.show({
        variant: 'success',
        title: t('tenant.handelMagazyn.fiscalPrint.remembered', 'Zapamiętano jako Twoją drukarkę'),
      });
    },
  });

  // Postęp zadania. Odpytujemy co sekundę, dopóki wydruk trwa — kasjer stoi przy kliencie.
  const jobQuery = useQuery({
    queryKey: ['fiscal', 'print-job', jobId, companyId],
    queryFn: () => getPrintJob(jobId!, companyId!),
    enabled: !!jobId && !!companyId,
    refetchInterval: (query) => {
      const data = query.state.data as FiscalPrintJobState | undefined;
      return data && !isPrintJobInProgress(data.status) ? false : 1000;
    },
  });

  const job = jobQuery.data;

  useEffect(() => {
    if (job?.status === 'printed') {
      toast.show({
        variant: 'success',
        title: t('tenant.handelMagazyn.fiscalPrint.printed', 'Paragon wydrukowany'),
      });
      onDone();
    }
  }, [job?.status, onDone, t, toast]);

  const queueMutation = useMutation({
    mutationFn: () =>
      queueReceiptPrint({
        documentId,
        companyId: companyId!,
        stationId,
        includeBuyerTaxId: withTaxId,
      }),
    onSuccess: (data) => {
      setMappingErrors([]);
      setJobId(data.jobId);
    },
    onError: (err) => {
      if (isAxiosError(err)) {
        const data = err.response?.data as
          | {
              error?: string;
              errors?: FiscalMappingErrorDto[];
              jobId?: string;
              receiptNumber?: string | null;
              printedAt?: string | null;
            }
          | undefined;
        // 422 — dokument nie nadaje się na paragon. To lista rzeczy do poprawienia, nie awaria.
        if (data?.error === 'RECEIPT_NOT_PRINTABLE' && data.errors) {
          setMappingErrors(data.errors);
          return;
        }
        /**
         * 409, wariant pierwszy: dokument MA JUŻ wydrukowany paragon.
         *
         * Nie pokazujemy ekranu postępu — ten powiedziałby „wydrukowano" i użytkownik
         * poszedłby szukać świeżego wydruku przy drukarce, z której nic nie wyszło.
         * Zamiast tego mówimy wprost: paragon do tego dokumentu już istnieje, oto jego numer.
         */
        if (data?.error === 'PRINT_ALREADY_DONE') {
          setAlreadyPrinted({
            receiptNumber: data.receiptNumber ?? null,
            printedAt: data.printedAt ?? null,
          });
          return;
        }
        // 409, wariant drugi: wydruk już trwa (dwa kliknięcia albo druga osoba).
        // Tu podpięcie się pod istniejące zadanie jest tym, czego użytkownik oczekuje.
        if (data?.error === 'PRINT_ALREADY_QUEUED' && data.jobId) {
          setJobId(data.jobId);
          return;
        }
      }
      toast.show({
        variant: 'error',
        title: t('tenant.handelMagazyn.fiscalPrint.failed', 'Nie udało się zlecić wydruku'),
      });
    },
  });

  // Dokument w buforze albo już zafiskalizowany nie daje się wydrukować — zamiast pokazywać
  // przycisk pod odmowę, nie pokazujemy go wcale (ta sama zasada co przy „Fiskalizuj").
  if (inBuffer || fiscalized || !companyId) return null;

  const stations = stationsQuery.data ?? [];
  const selected = stations.find((s) => s.id === stationId);

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid="hm-fiscal-print-open"
      >
        <Printer className="mr-1.5 h-4 w-4" />
        {t('tenant.handelMagazyn.fiscalPrint.action', 'Drukuj paragon')}
      </Button>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setJobId(null);
          setMappingErrors([]);
          setAlreadyPrinted(null);
        }}
        title={t('tenant.handelMagazyn.fiscalPrint.title', 'Wydruk paragonu fiskalnego')}
      >
        <div className="space-y-4">
          {alreadyPrinted && (
            <div
              className="rounded border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-800 dark:border-sky-800 dark:bg-sky-900/20 dark:text-sky-300"
              role="status"
              data-testid="hm-fiscal-already-printed"
            >
              <p className="mb-1 font-medium">
                {t(
                  'tenant.handelMagazyn.fiscalPrint.alreadyPrintedTitle',
                  'Ten dokument ma już paragon',
                )}
              </p>
              <p>
                {alreadyPrinted.receiptNumber
                  ? t('tenant.handelMagazyn.fiscalPrint.alreadyPrintedWithNumber', {
                      defaultValue:
                        'Paragon nr {{number}} został wydrukowany {{when}}. Drugiego paragonu do tego samego dokumentu wystawić się nie da — trafiłby do pamięci fiskalnej jako osobna sprzedaż.',
                      number: alreadyPrinted.receiptNumber,
                      when: formatPrintedAt(alreadyPrinted.printedAt),
                    })
                  : t(
                      'tenant.handelMagazyn.fiscalPrint.alreadyPrintedNoNumber',
                      'Paragon do tego dokumentu już powstał. Drugiego wystawić się nie da — trafiłby do pamięci fiskalnej jako osobna sprzedaż.',
                    )}
              </p>
              <div className="mt-3 flex justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setOpen(false);
                    setAlreadyPrinted(null);
                  }}
                  data-testid="hm-fiscal-already-printed-close"
                >
                  {t('common.close', 'Zamknij')}
                </Button>
              </div>
            </div>
          )}

          {!jobId && !alreadyPrinted && (
            <>
              <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                {t(
                  'tenant.handelMagazyn.fiscalPrint.warning',
                  'Paragon trafia do pamięci fiskalnej drukarki i NIE DA SIĘ GO COFNĄĆ.',
                )}
              </p>

              <div>
                <label
                  className="mb-1 block text-sm font-medium text-text-primary"
                  htmlFor="hm-fiscal-station"
                >
                  {t('tenant.handelMagazyn.fiscalPrint.station', 'Stanowisko z drukarką')}
                </label>
                {stationsQuery.isLoading ? (
                  <Spinner size="sm" />
                ) : stations.length === 0 ? (
                  <p className="text-sm text-text-secondary" data-testid="hm-fiscal-no-stations">
                    {t(
                      'tenant.handelMagazyn.fiscalPrint.noStations',
                      'Nie ma jeszcze żadnego stanowiska z drukarką — skonfiguruj je w ustawieniach modułu.',
                    )}
                  </p>
                ) : (
                  <Select
                    id="hm-fiscal-station"
                    value={stationId}
                    onChange={(e) => setStationId(e.target.value)}
                    placeholder={t('tenant.handelMagazyn.fiscalPrint.choose', 'Wybierz…')}
                    data-testid="hm-fiscal-station-select"
                    options={stations.map((station) => ({
                      value: station.id,
                      // Stan usługi w etykiecie, nie tylko w podpowiedzi pod spodem: przy kilku
                      // kasach trzeba widzieć OD RAZU, która żyje.
                      label: station.online
                        ? station.name
                        : `${station.name} — ${t('tenant.handelMagazyn.fiscalPrint.offline', 'usługa nie odpowiada')}`,
                      disabled: !station.isActive,
                    }))}
                  />
                )}
                {selected && myStationQuery.data?.stationId !== selected.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1"
                    onClick={() => rememberMutation.mutate()}
                    data-testid="hm-fiscal-remember-station"
                  >
                    {t(
                      'tenant.handelMagazyn.fiscalPrint.remember',
                      'Ustaw jako moją domyślną drukarkę',
                    )}
                  </Button>
                )}
                {selected && !selected.online && (
                  <p
                    className="mt-1 text-sm text-amber-700 dark:text-amber-300"
                    data-testid="hm-fiscal-station-offline"
                  >
                    {t(
                      'tenant.handelMagazyn.fiscalPrint.offlineHint',
                      'Usługa przy tej drukarce nie zgłasza się. Wydruk poczeka w kolejce, aż wróci.',
                    )}
                  </p>
                )}
              </div>

              {buyerTaxId && (
                <div>
                  <Checkbox
                    checked={withTaxId}
                    onChange={(e) => setWithTaxId(e.target.checked)}
                    label={t(
                      'tenant.handelMagazyn.fiscalPrint.withTaxId',
                      'Nadrukuj NIP nabywcy na paragonie',
                    )}
                    data-testid="hm-fiscal-with-taxid"
                  />
                  <p className="mt-1 text-xs text-text-secondary">
                    {t(
                      'tenant.handelMagazyn.fiscalPrint.taxIdHint',
                      'Decyzja zapada teraz — po wydruku NIP-u nie da się dopisać, a bez niego nie powstanie faktura do tego paragonu.',
                    )}
                  </p>
                </div>
              )}

              {mappingErrors.length > 0 && (
                <div
                  className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
                  data-testid="hm-fiscal-mapping-errors"
                  role="alert"
                >
                  <p className="mb-1 font-medium">
                    {t(
                      'tenant.handelMagazyn.fiscalPrint.notPrintable',
                      'Tego dokumentu nie da się wydrukować jako paragon:',
                    )}
                  </p>
                  <ul className="list-disc pl-5">
                    {mappingErrors.map((error, index) => (
                      <li key={`${error.code}-${error.positionNo ?? index}`}>{error.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  disabled={!stationId || queueMutation.isPending}
                  onClick={() => {
                    // Rezygnacja z NIP-u przy dokumencie, który go niesie, ma nieodwracalny
                    // skutek (brak faktury do paragonu) — pytamy wprost, zamiast zakładać.
                    if (buyerTaxId && !withTaxId) {
                      setConfirmNoTaxId(true);
                      return;
                    }
                    queueMutation.mutate();
                  }}
                  data-testid="hm-fiscal-print-submit"
                >
                  {t('tenant.handelMagazyn.fiscalPrint.submit', 'Drukuj')}
                </Button>
              </div>
            </>
          )}

          {jobId && job && <PrintProgress job={job} />}
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmNoTaxId}
        variant="primary"
        title={t('tenant.handelMagazyn.fiscalPrint.noTaxIdTitle', 'Paragon bez NIP-u nabywcy')}
        message={t(
          'tenant.handelMagazyn.fiscalPrint.noTaxIdMessage',
          'Dokument ma NIP nabywcy, ale paragon pójdzie bez niego. Do takiego paragonu NIE DA SIĘ później wystawić faktury, a po wydruku nic już nie poprawisz. Drukować bez NIP-u?',
        )}
        confirmLabel={t('tenant.handelMagazyn.fiscalPrint.noTaxIdConfirm', 'Drukuj bez NIP-u')}
        onConfirm={() => {
          setConfirmNoTaxId(false);
          queueMutation.mutate();
        }}
        onClose={() => setConfirmNoTaxId(false)}
      />
    </>
  );
}

/** Data wydruku paragonu. Brak daty zdarza się przy starszych zadaniach — nie udajemy, że ją znamy. */
function formatPrintedAt(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pl-PL');
}

/**
 * Postęp wydruku. Stan „nie wiadomo" ma WŁASNY komunikat i własny kolor — zrównanie go
 * z błędem podpowiadałoby „kliknij jeszcze raz", czyli dokładnie to, czego nie wolno zrobić.
 */
function PrintProgress({ job }: { job: FiscalPrintJobState }) {
  const { t } = useTranslation();

  if (isPrintJobInProgress(job.status)) {
    return (
      <div className="flex items-center gap-2 text-sm" data-testid="hm-fiscal-progress">
        <Spinner size="sm" />
        {job.status === 'sent'
          ? t('tenant.handelMagazyn.fiscalPrint.printing', 'Drukowanie…')
          : t('tenant.handelMagazyn.fiscalPrint.queued', 'Zadanie czeka na stanowisko…')}
      </div>
    );
  }

  if (job.status === 'printed') {
    return (
      <div className="text-sm text-green-700 dark:text-green-400" data-testid="hm-fiscal-printed">
        {t('tenant.handelMagazyn.fiscalPrint.printedWithNumber', 'Paragon wydrukowany, numer:')}{' '}
        <strong>{job.receiptNumber}</strong>
      </div>
    );
  }

  if (job.status === 'unknown') {
    return (
      <div
        className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
        data-testid="hm-fiscal-unknown"
        role="alert"
      >
        {t(
          'tenant.handelMagazyn.fiscalPrint.unknown',
          'Nie wiadomo, czy paragon został wydrukowany — usługa nie zdążyła zgłosić wyniku. Sprawdź drukarkę i rozstrzygnij w ustawieniach stanowisk. NIE drukuj ponownie, dopóki tego nie zrobisz.',
        )}
      </div>
    );
  }

  return (
    <div
      className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
      data-testid="hm-fiscal-error"
      role="alert"
    >
      {job.errorMessage ??
        t('tenant.handelMagazyn.fiscalPrint.refused', 'Drukarka odmówiła wydruku.')}
    </div>
  );
}
